import { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "config/api";

const normalizeFrontdesk = (frontdesk) => {
  if (!frontdesk || typeof frontdesk !== "object") {
    return {
      id: "",
      name: "",
      email: "",
      phone: "",
      whatsapp: "",
      clinicName: "",
      status: "",
      avatarUrl: "",
      createdAt: "",
    };
  }

  const frontdeskId = frontdesk.id || frontdesk._id || frontdesk.frontdesk_id || "";
  const firstname = frontdesk.firstname || "";
  const lastname = frontdesk.lastname || "";
  const name = `${firstname} ${lastname}`.trim() || frontdesk.name || "";
  const email = frontdesk.email || "";
  const phone = frontdesk.phoneno || frontdesk.phone || frontdesk.phone_no || "";
  const whatsapp = frontdesk.whatsapp_no || frontdesk.whatsapp || "";
  const clinicName = frontdesk.clinic_name || frontdesk.clinicName || "";
  const clinicId = frontdesk.clinic_id || frontdesk.clinicId || "";
  const status = frontdesk.status || "active";
  const avatar = frontdesk.avatar_url || frontdesk.avatarUrl || "";
  const createdAt = frontdesk.created_at || frontdesk.createdAt || frontdesk.regdate || "";

  return {
    id: frontdeskId ? String(frontdeskId) : "",
    clinicId: clinicId ? String(clinicId) : "",
    firstname,
    lastname,
    name,
    email,
    phone,
    whatsapp,
    clinicName,
    status,
    avatarUrl: avatar,
    createdAt,
  };
};

export const useFrontdeskList = (clinicId = null) => {
  const [frontdesks, setFrontdesks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFrontdesks = useCallback(
    async (signal) => {
      setIsLoading(true);
      try {
          const response = await axiosInstance.get("/api/frontdesk/get", {
          validateStatus: (status) => status < 500,
          params: clinicId ? { clinic_id: clinicId } : {},
          // allow callers to cancel if they pass a signal
          signal,
        });

        let frontdeskArray = [];
        const { data } = response;

        // Support multiple response shapes returned by different API versions
        if (Array.isArray(data)) {
          frontdeskArray = data;
        } else if (data && Array.isArray(data.frontdesks)) {
          frontdeskArray = data.frontdesks;
        } else if (data && Array.isArray(data.front_desks)) {
          // some endpoints return "front_desks"
          frontdeskArray = data.front_desks;
        } else if (data && Array.isArray(data.data)) {
          frontdeskArray = data.data;
        } else if (data && typeof data === "object") {
          frontdeskArray = Object.values(data).filter(
            (value) => value && typeof value === "object"
          );
        }

        const normalizedFrontdesks = frontdeskArray.map(normalizeFrontdesk);
        setFrontdesks(normalizedFrontdesks);
        setError(null);
      } catch (err) {
        // If the request was cancelled we don't want to set error state
        if (err.name === 'CanceledError' || err.message === 'canceled') {
          return;
        }
        console.error("Error fetching frontdesks:", err);
        setFrontdesks([]);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [clinicId]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchFrontdesks(controller.signal);
    return () => controller.abort();
  }, [fetchFrontdesks, clinicId]);

  return {
    frontdesks,
    setFrontdesks,
    isLoading,
    error,
    refreshFrontdesks: fetchFrontdesks,
  };
};
